import {
  DEFAULT_BOOKING_WINDOW_DAYS,
  exceedsMinuteCap,
  MAX_BUFFER_MINUTES,
  MAX_DURATION_MINUTES,
  MAX_MINIMUM_NOTICE_MINUTES,
  type AppointmentTypeDto,
  type CappedMinuteField,
  type LocationType,
} from '@shared/core/appointment-types'

export type NoticeUnit = 'minutes' | 'hours' | 'days'

/**
 * The one genuinely duplicated helper between the settings modal and the
 * onboarding wizard. Its siblings (a parser and a display formatter) have
 * different signatures and deliberately stay where they are.
 */
export function formDataToMinutes(value: number, unit: NoticeUnit): number {
  switch (unit) {
    case 'days':
      return value * 24 * 60
    case 'hours':
      return value * 60
    case 'minutes':
    default:
      return value
  }
}

export const MINUTE_CAP_MESSAGES: Record<CappedMinuteField, string> = {
  duration_minutes: `Duration can be at most ${MAX_DURATION_MINUTES} minutes (7 days)`,
  buffer_before_minutes: `Buffer before can be at most ${MAX_BUFFER_MINUTES} minutes (1 day)`,
  buffer_after_minutes: `Buffer after can be at most ${MAX_BUFFER_MINUTES} minutes (1 day)`,
  minimum_notice_minutes: `Minimum notice can be at most ${MAX_MINIMUM_NOTICE_MINUTES / (24 * 60)} days`,
}

/** A react-hook-form `validate` result. `storedMinutes` is the saved value, which stays saveable over the cap. */
export function validateMinuteCap(field: CappedMinuteField, minutes: number, storedMinutes?: number): true | string {
  return exceedsMinuteCap(field, minutes, storedMinutes) ? MINUTE_CAP_MESSAGES[field] : true
}

/**
 * Two named options rather than a clearable number field: an empty box that
 * silently means "no limit" gives the owner no way to tell unlimited apart
 * from "I forgot to fill this in".
 */
export type BookingWindowMode = 'indefinite' | 'days'

export const bookingWindowModeOptions: { value: BookingWindowMode; label: string }[] = [
  { value: 'indefinite', label: 'Indefinitely into the future' },
  { value: 'days', label: 'calendar days into the future' },
]

export const BOOKING_WINDOW_LABEL = 'Booking Window'
export const BOOKING_WINDOW_HELPER_TEXT = 'How far into the future customers can see availability and book.'

/** An unset window renders as the named "indefinitely" option, never as 0. */
export function bookingWindowToFormData(days: number | null | undefined): {
  mode: BookingWindowMode
  days: number
} {
  if (days === null || days === undefined) {
    return { mode: 'indefinite', days: DEFAULT_BOOKING_WINDOW_DAYS }
  }
  return { mode: 'days', days }
}

export function formDataToBookingWindow(mode: BookingWindowMode, days: number): number | null {
  return mode === 'days' ? days : null
}

/**
 * Non-blocking: a window that closes before the notice elapses leaves nothing
 * bookable, but the owner may be mid-edit and is not stopped from saving.
 */
export function shouldWarnWindowShorterThanNotice(
  bookingWindowDays: number | null,
  minimumNoticeMinutes: number,
): boolean {
  if (bookingWindowDays === null) return false
  // The window ends at local midnight on today + (N - 1).
  const windowEndMinutesFromNow = bookingWindowDays * 24 * 60
  return windowEndMinutesFromNow < minimumNoticeMinutes
}

export const LOCATION_LABEL = 'Location'

export const LOCATION_OPTION_COPY: Record<LocationType, { label: string; description: string }> = {
  none: { label: 'None', description: 'No location is shown to customers' },
  google_meet: { label: 'Google Meet video call', description: 'A unique Meet link is created for every booking' },
  in_person: { label: 'In-person address', description: 'Your address, entered once here' },
  phone: { label: 'Phone call', description: "You'll call the customer at the number they book with" },
  customer_address: { label: "Customer's address", description: 'The customer enters their address when booking' },
  custom: { label: 'Custom', description: 'Any text, shown to customers as written' },
}

export const LOCATION_HELPER_NOTES: Partial<Record<LocationType, string>> = {
  google_meet:
    'A unique Google Meet link is created for every booking and attached to the calendar event. Customers see it on the confirmation page and in emails.',
  phone: "You'll call the customer at the number they book with. Nothing else to set up.",
  customer_address:
    "Customers must enter their address to book. It's added to the calendar event, so reminders that use {eventlocation} tell you where to go.",
}

export type ValueLocationType = Extract<LocationType, 'in_person' | 'custom'>

export const LOCATION_VALUE_REQUIRED_MESSAGES: Record<ValueLocationType, string> = {
  in_person: 'Address is required',
  custom: 'Location text is required',
}

/** Matches the server cap on `location_value` (S-633). */
export const LOCATION_VALUE_MAX_LENGTH = 500

export const LOCATION_VALUE_INPUT_COPY: Record<
  ValueLocationType,
  { label: string; placeholder: string; hint: string }
> = {
  in_person: {
    label: 'Address',
    placeholder: '120 King St W, Suite 400, Toronto, ON',
    hint: 'Customers see this on the confirmation page, in emails and in reminders that use {eventlocation}. Opens in Maps when tapped.',
  },
  custom: {
    label: 'Location details',
    placeholder: 'e.g. Coworking hub, 2nd floor. Enter through the back gate',
    hint: 'Shown to customers exactly as written.',
  },
}

export const TEMPLATE_HINT_MISSING_TOKEN = "doesn't include"
export const TEMPLATE_HINT_CONSEQUENCE = ", so reminders won't say where the appointment is."
export const EDIT_TEMPLATE_LABEL = 'Edit template'
export const SAVE_APPOINTMENT_TYPE_ERROR_FALLBACK = 'Failed to save appointment type. Please try again.'

export function meetUnavailableText(calendarName: string): string {
  return `Not available: ${calendarName} can't host Google Meet`
}

export type LocationFormData = {
  locationType: LocationType
  inPersonAddress: string
  customLocationText: string
}

/** Each typed input keeps its own text, so switching options never carries an address into Custom. */
export const LOCATION_VALUE_FIELDS: Record<ValueLocationType, 'inPersonAddress' | 'customLocationText'> = {
  in_person: 'inPersonAddress',
  custom: 'customLocationText',
}

export function locationRequiresValue(locationType: LocationType): locationType is ValueLocationType {
  return locationType in LOCATION_VALUE_FIELDS
}

export function locationToFormData(
  appointmentType?: Pick<AppointmentTypeDto, 'location_type' | 'location_value'>,
): LocationFormData {
  const locationType = appointmentType?.location_type ?? 'none'
  const formData: LocationFormData = { locationType, inPersonAddress: '', customLocationText: '' }
  if (locationRequiresValue(locationType)) {
    formData[LOCATION_VALUE_FIELDS[locationType]] = appointmentType?.location_value ?? ''
  }
  return formData
}

export function formDataToLocation(data: LocationFormData): {
  location_type: LocationType
  location_value: string | null
} {
  const { locationType } = data
  const locationValue = locationRequiresValue(locationType) ? data[LOCATION_VALUE_FIELDS[locationType]] : null
  return { location_type: locationType, location_value: locationValue }
}
